import React, { forwardRef } from "react";
import { useNavigate } from "react-router-dom";

import DeleteIconErrorSvg from "../../assets/images/icon-delete-error.svg";
import { colors } from "../../assets/styles/theme";
import {
  Box,
  Autocomplete,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  Switch,
  Alert,
  Link,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import { useGetConnectionsDataSources } from "../hooks";

interface PlatformSectionProps {
  onDelete: (id: any) => void;
  onFieldChange: (id: any, type: any, value: any) => void;
  onFieldBlur: (id: any, type: any) => void;
  section: any;
  onToggleActive: (section: any) => void;
  availablePlatforms: any;
  toggleEditMode: (id: any) => void;
  handleSubmit: (id: any) => void;
  cancelEdit: (id: any) => void;
  isSaving: any;
  checkForDuplicates: (values: any) => void;
  isEditDisable: any;
  isNewSection: any;
  highlight: any;
}

const PlatformSection = forwardRef((props: any, ref: any) => {
  const {
    section,
    onDelete,
    onFieldChange,
    onFieldBlur,
    onToggleActive,
    availablePlatforms,
    toggleEditMode,
    handleSubmit,
    cancelEdit,
    isSaving,
    checkForDuplicates,
    isEditDisable,
    isNewSection,
    highlight,
  } = props;

  const navigate = useNavigate();

  const { data: connections, isLoading: isConnectionsLoading } =
    useGetConnectionsDataSources(section.platform ? section.platform : null);

  const hasSectionErrors = (section: any) => {
    // Check for required field errors
    const hasRequiredFieldErrors =
      !section.platform || !section.connection || !section.accountId;

    // Get the errors object with a default empty object if it doesn't exist
    const errors = section.errors || {};

    // Check for the specific error fields you mentioned
    const hasSpecificErrors =
      errors.uniqueCombo !== undefined ||
      errors.duplicatePlatform !== undefined ||
      errors.duplicateConnection !== undefined ||
      errors.duplicateAccountId !== undefined;

    // Check for any other validation errors in the errors object
    const hasOtherValidationErrors =
      Object.keys(errors).filter(
        (key) =>
          key !== "uniqueCombo" &&
          key !== "duplicatePlatform" &&
          key !== "duplicateConnection" &&
          key !== "duplicateAccountId"
      ).length > 0;

    return (
      hasRequiredFieldErrors || hasSpecificErrors || hasOtherValidationErrors
    );
  };

  return (
    <React.Fragment key={section.id}>
      <Box className={highlight ? "" : ""} ref={ref}>
        <Grid container spacing={0} sx={{ mb: 1.6 }} >
          <Grid container spacing={0} sx={{}}>
            <Grid xs={6} item>
              <Switch
                checked={section.status}
                disabled={!section.isEditing}
                onChange={() => onToggleActive(section)}
              />{" "}
              <Typography variant="body2" className="fs-16" component="span">
                Active
              </Typography>
            </Grid>
            {!section.isEditing && (
              <Grid
                xs={6}
                item
                className="d-flex justify-content-end align-items-center"
              >
                <span
                  className="editPlatformLink"
                  aria-disabled={isEditDisable}
                  onClick={() => toggleEditMode(section.id)}
                >
                  <Link
                    className="fs-16 d-flex align-items-center"
                    sx={{ textDecoration: "none" }}
                  >
                    <EditIcon fontSize="small" /> &nbsp;Edit
                  </Link>
                </span>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid xs={7} item>
            <Grid container spacing={2} sx={{ mb: 2.2 }}>
              <Grid xs={5} item>
                <Autocomplete
                  value={
                    availablePlatforms?.find(
                      (option: any) =>
                        String(option.value) === String(section.platform)
                    ) || null
                  }
                  disabled={!section.isEditing}
                  onChange={(event, newValue) => {
                    onFieldChange(
                      section.id,
                      "platform",
                      newValue?.value || ""
                    );
                  }}
                  onBlur={() => onFieldBlur(section.id, "platform")}
                  options={availablePlatforms || []}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Data Source"
                      variant="outlined"
                      error={
                        (section.touched?.platform &&
                          Boolean(section.errors?.platform)) ||
                        Boolean(section.errors?.duplicatePlatform)
                      }
                      helperText={
                        section.touched?.platform &&
                        section.errors?.platform ? (
                          section.errors.platform
                        ) : section.errors?.duplicatePlatform ? null : (
                          <span>
                            Required<span style={{ color: "red" }}>*</span>
                          </span>
                        )
                      }
                    />
                  )}
                />
              </Grid>

              <Grid xs={7} item>
                <Autocomplete
                  fullWidth
                  value={
                    connections?.find(
                      (option: any) =>
                        String(option.value) === String(section.connection)
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    onFieldChange(
                      section.id,
                      "connection",
                      newValue?.value || ""
                    );
                  }}
                  disabled={
                    !section.platform ||
                    isConnectionsLoading ||
                    !section.isEditing
                  }
                  options={connections || []}
                  getOptionLabel={(option) => option.label}
                  loading={isConnectionsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Connection"
                      error={
                        (section.touched?.connection &&
                          Boolean(section.errors?.connection)) ||
                        Boolean(section.errors?.duplicateConnection)
                      }
                      helperText={
                        section.touched?.connection &&
                        section.errors?.connection ? (
                          section.errors.connection
                        ) : section.errors?.duplicateConnection ? null : (
                          <span>
                            Required<span style={{ color: "red" }}>*</span>
                          </span>
                        )
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isConnectionsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2.5} sx={{ mb: 2.2 }}>
              <Grid xs={12} item>
                <TextField
                  fullWidth
                  label="Account ID"
                  disabled={!section.isEditing}
                  placeholder="Account ID"
                  value={section.accountId}
                  onBlur={() => onFieldBlur(section.id, "accountId")}
                  onChange={(e) => {
                    onFieldChange(section.id, "accountId", e.target.value);
                  }}
                  error={
                    (section.touched?.accountId &&
                      Boolean(section.errors?.accountId)) ||
                    Boolean(section.errors?.duplicateAccountId)
                  }
                  helperText={
                    section.touched?.accountId && section.errors?.accountId ? (
                      section.errors.accountId
                    ) : section.errors?.duplicateAccountId ? null : (
                      <span>
                        Required<span style={{ color: "red" }}>*</span>
                      </span>
                    )
                  }
                ></TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid xs={5} item>
            <TextField
              fullWidth
              label="Description"
              placeholder="This is the description."
              value={section.description}
              disabled={!section.isEditing}
              onChange={(e) =>
                onFieldChange(section.id, "description", e.target.value)
              }
              onBlur={() => onFieldBlur(section.id, "description")}
              error={
                section.touched?.description &&
                Boolean(section.errors?.description)
              }
              helperText={
                section.touched?.description && section.errors?.description
              }
              InputLabelProps={{
                shrink: true,
              }}
              multiline
              rows={4.5}
            ></TextField>
          </Grid>
        </Grid>
      </Box>
      {/* <Divider sx={{ mt: 1, mb: 2 }} /> */}

      {section?.errors?.uniqueCombo && (
        <Grid container spacing={2.5}>
          <Grid xs={12} item>
            {section?.errors?.uniqueCombo && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {section?.errors?.uniqueCombo}
              </Alert>
            )}
          </Grid>
        </Grid>
      )}

      {(section.successMessage || section.errorMessage) && (
        <Grid container spacing={2.5}>
          <Grid xs={12} item>
            {section.errorMessage && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {section.errorMessage}.
              </Alert>
            )}

            {section.successMessage && (
              <Alert severity="success" sx={{ mb: 1 }}>
                {section.successMessage}.
              </Alert>
            )}
          </Grid>
        </Grid>
      )}

      {section.isEditing && (
        <>
          {/* <Divider sx={{ mt: 1, mb: 2 }} /> */}
          <Box className="d-flex justify-content-between" sx={{ mb: 3 }}>
            <Box>
              {!section.isNew && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mr: 2 }}
                  color="error"
                  onClick={() => onDelete(section)}
                >
                  <img src={DeleteIconErrorSvg} alt="Delete Icon" /> &nbsp;
                  DELETE
                </Button>
              )}
            </Box>
            <Box className="d-flex justify-content-end ">
              <Button
                variant="text"
                onClick={() => cancelEdit(section.id)}
                size="small"
                disabled={isSaving}
                sx={{ mr: 2 }}
              >
                CANCEL
              </Button>
              <Button
                variant="contained"
                disabled={
                  isSaving ||
                  hasSectionErrors(section) ||
                  checkForDuplicates(section)
                }
                onClick={() => handleSubmit(section.id)}
                size="small"
              >
                SAVE{" "}
                {isSaving && (
                  <CircularProgress
                    size="1rem"
                    sx={{ color: colors.primaryDark, ml: 1 }}
                  />
                )}
              </Button>
            </Box>
          </Box>
          {/* <Divider sx={{ mt: 1, mb: 2 }} /> */}
        </>
      )}
      <Divider sx={{ mt: 2, mb: 3 }} />
    </React.Fragment>
  );
});

export default PlatformSection;
